<script lang="ts">
  import { Router, Route } from "svelte-routing";
  import { ROUTE, VALID_ROUTES, VALID_ROUTES_WITH_ID, type ValidRoute, type ValidRouteWithId } from "./constants/route-constants.js";
  import Main from "./pages/Main.svelte";
  import LoginFake from "./pages/LoginFake.svelte";
  import LoginB2C from "./pages/LoginB2C.svelte";
  import PasswordResetB2C from "./pages/PasswordResetB2C.svelte";
  import { isAuthenticated, redirectUrl } from "./services/security/auth-handler.js";
  import { updateCurrentRoute } from "./services/routing/route-action.js";
  import { currentRoute } from "./services/routing/route-store.js";
  import { theme } from "./services/stores/theme-store.js";
  import { userStore } from "./services/stores/user-store.js";
  import { getEnvironment } from "./config/env.js";
  import { checkRouteAccess } from "./services/security/route-guard.js";
  import { onMount } from "svelte";
  import { resetChatStore, setChatUserContext } from "./services/stores/chat-store.js";
  import { checkVersionAndCleanup, getAppVersion } from "./services/cache/cache-manager.js";
  import { logInfo } from "./services/logging/log-handler.js";
  import ChatbotPage from "./pages/ChatbotPage.svelte";
  import DocumentExtractPage from "./pages/DocumentExtractPage.svelte";
  import ProjectExtractPage from "./pages/ProjectExtractPage.svelte";

  // CRITICAL: Check for B2C/MSAL redirect FIRST, before any other logic
  // B2C redirects to the root URL with hash containing state/code
  // If we detect this, we need to preserve the hash for LoginB2C to process
  const hash = window.location.hash;
  const isMsalRedirect = hash.includes('state=') || hash.includes('code=') || hash.includes('error=');
  let redirectingToLogin = false;
  
  if (isMsalRedirect) {
    logInfo("Detected MSAL redirect hash at startup", "App", undefined, { hash: hash.substring(0, 100) });
    // If we're at the root and there's an MSAL hash, redirect to /login-b2c while preserving the hash
    // This ensures LoginB2C can process the authentication response
    if (window.location.pathname === '/' || window.location.pathname === '') {
      redirectingToLogin = true;
      window.location.href = '/login-b2c' + hash;
      // Stop execution - page will reload at /login-b2c with the hash
    }
  }

  let auth = redirectingToLogin ? false : localStorage.getItem("auth") === "true";
  // Track current user ID to detect user switches and scope chat storage
  let currentUserId: string | null = null;
  // Track if we're waiting for version check/cleanup
  let versionCheckPending = true;

  // Check for new version and clear cache if needed (runs synchronously to block rendering)
  // This ensures users always get the latest version without manual cache clearing
  logInfo(`Starting with app version`, "App", undefined, { appVersion: getAppVersion() });

  isAuthenticated.set(auth); // Set the store value to match storage state
  const env = getEnvironment();

  // Step 2: Restore user data from storage (synchronous)
  if (auth) {
    const object_id = localStorage.getItem("object_id");
    if (!object_id) {
      auth = false;
      isAuthenticated.set(false);
      userStore.clearUser();
      localStorage.removeItem("auth");
      sessionStorage.removeItem("auth");
      // Set chat context to null (no user)
      setChatUserContext(null);
    } else {
      const storedUser = localStorage.getItem("user");
      const clearAuthState = () => {
        auth = false;
        isAuthenticated.set(false);
        userStore.clearUser();
        localStorage.removeItem("auth");
        sessionStorage.removeItem("auth");
        // Set chat context to null (no user)
        setChatUserContext(null);
      };

      if (storedUser && storedUser !== "{}" && storedUser.trim() !== "") {
        const parsedUser = JSON.parse(storedUser);
        // Check if parsed user has actual content (not just an empty object)
        if (parsedUser && Object.keys(parsedUser).length > 0) {
          userStore.setUser(parsedUser);
          // Set chat context to current user - this loads user-scoped chat history
          currentUserId = object_id;
          setChatUserContext(object_id);
        } else {
          clearAuthState();
        }
      } else {
        clearAuthState();
      }
    }
  } else {
    // Not authenticated - ensure chat context is cleared
    setChatUserContext(null);
  }

  onMount(async () => {
    // Check for persistent logs from previous cleanup (survives page reloads)
    // Show them immediately so they're visible even if page reloads
    try {
      const hasLogs = localStorage.getItem('cache_manager_logs');
      if (hasLogs) {
        // Use a small delay to ensure console is ready
        setTimeout(() => {
          showPersistentLogs();
        }, 50);
      }
    } catch (_) {}
    
    // Check for version change and clear cache if new version deployed
    // This ensures users always see the latest version without manual cache clearing
    try {
      const versionChanged = await checkVersionAndCleanup();
      if (versionChanged) {
        // Page will reload, don't continue initialization
        return;
      }
    } catch (error) {
      logWarn("Version check warning", "App", undefined, { error: error });
    }
    versionCheckPending = false;

    if (auth) {
      const path = window.location.pathname;
      const routeName = path.split("/")[1] || ROUTE.HOME;
      const queryParams = new URLSearchParams(window.location.search);
      const id = queryParams.get("id");
      if (VALID_ROUTES.includes(routeName as ValidRoute)) {
        if (
          id &&
          VALID_ROUTES_WITH_ID.includes(routeName as ValidRouteWithId)
        ) {
          updateCurrentRoute(routeName, id);
        } else {
          updateCurrentRoute(routeName);
        }
      } else {
        updateCurrentRoute(ROUTE.HOME);
      }
    }
  });

  $: {
    isAuthenticated.subscribe((value) => {
      const oldAuth = auth;
      auth = value;

      // Ensure storage is in sync
      if (auth) {
        localStorage.setItem("auth", "true");
        sessionStorage.setItem("auth", "true");
      } else {
        localStorage.removeItem("auth");
        sessionStorage.removeItem("auth");
      }

      // Handle user context changes for scoped chat storage
      const newUserId = auth ? localStorage.getItem("object_id") : null;
      const userChanged = newUserId !== currentUserId;
      
      if (userChanged) {
        currentUserId = newUserId;
        // Switch to user-scoped storage (loads their chat history or empty state)
        setChatUserContext(newUserId);
        // Clear chat ID mappings for the new user context
        try {
          localStorage.removeItem("chatIdMap");
        } catch {}
      }

      // Legacy: Clear any persisted chat state on login transition
      // (This is now handled by setChatUserContext, but kept for safety)
      if (!oldAuth && auth) {
        try {
          resetChatStore();
        } catch {}
      }

      let currentRouteName = window.location.pathname.split("/")[1] || "";
      let currentId =
        window.location.search.split("?")[1]?.split("=")[1] ?? undefined;

      // Use route guard to check access
      const guardResult = checkRouteAccess({
        name: currentRouteName || ROUTE.HOME,
        params: currentId ? { id: currentId } : undefined,
      });

      if (!guardResult.isAllowed) {
        if (guardResult.redirectTo) {
          if (currentRouteName !== ROUTE.LOGIN) {
            if (VALID_ROUTES.includes(currentRouteName as ValidRoute)) {
              let currentUrl = "/" + currentRouteName;
              if (
                VALID_ROUTES_WITH_ID.includes(
                  currentRouteName as ValidRouteWithId,
                ) &&
                currentId
              ) {
                currentUrl += "?id=" + currentId;
              }
              redirectUrl.set(currentUrl);
            } else {
              redirectUrl.set("/");
            }
          }
          updateCurrentRoute(guardResult.redirectTo.replace("/", ""));
        }
      } else {
        // Handle post-authentication routing for allowed routes
        redirectUrl.subscribe((url) => {
          if (currentRouteName === ROUTE.LOGIN) {
            if (url && url !== "/" && url !== "undefined") {
              const routeName = url.split("?")[0].replace(/^\//, "");
              if (VALID_ROUTES.includes(routeName as ValidRoute)) {
                const urlId = url.split("?")[1]?.split("=")[1];
                if (
                  urlId &&
                  VALID_ROUTES_WITH_ID.includes(routeName as ValidRouteWithId)
                ) {
                  updateCurrentRoute(routeName, urlId);
                } else {
                  updateCurrentRoute(routeName);
                }
              } else {
                updateCurrentRoute(ROUTE.HOME);
              }
            } else {
              updateCurrentRoute(ROUTE.HOME);
            }
            redirectUrl.set("");
          }
        });
      }
    });
  }
</script>

<svelte:head>
  <link rel="stylesheet" href="/assets/styles/bundle-{$theme}.css" />
</svelte:head>

{#if redirectingToLogin}
  <!-- Show loading while redirecting to preserve MSAL hash -->
  <div class="flex items-center justify-center min-h-screen">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span class="ml-3">Completing authentication...</span>
  </div>
{:else}
  <Router url={window.location.pathname + window.location.search}>
    {#if auth}
      <Route path="/chatbot" component={ChatbotPage} />
      <Route path="/document-extract" component={DocumentExtractPage} />
      <Route path="/project-extract" component={ProjectExtractPage} />
      <Route path="/*" component={Main} />
    {:else if $currentRoute.name === ROUTE.PASSWORD_RESET}
      <Route component={env.useB2C ? PasswordResetB2C : LoginFake} />
    {:else}
      <Route component={env.useB2C ? LoginB2C : LoginFake} />
    {/if}
  </Router>
{/if}
